<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateBrokerTaxDocumentForm from '../components/RealEstateBrokerTaxDocumentForm.vue';
import RealEstateBrokerTaxDocumentSuccessDialog from '../components/RealEstateBrokerTaxDocumentSuccessDialog.vue';
import { useRealEstateBrokerCommissionRequest } from '../composables/useRealEstateBrokerCommissionRequest';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const installmentId = computed(() => route.params.installmentId);
const state = useRealEstateBrokerCommissionRequest({
  installmentId,
  notify: useAlert,
  t,
});
const successDialogVisible = ref(false);

const accountParams = computed(() => ({ accountId: route.params.accountId }));
const goBack = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor_parcela',
    params: { ...accountParams.value, installmentId: installmentId.value },
  });
const returnToDashboard = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor',
    params: accountParams.value,
  });
const submit = async () => {
  if (await state.submitDocument()) successDialogVisible.value = true;
};
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.PAGE_TITLE')"
      :subtitle="t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.PAGE_SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_BROKER_DASHBOARD.TITLE') },
        { label: t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.PAGE_TITLE') },
      ]"
      show-back
      :back-label="t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.BACK')"
      @back="goBack"
    />
    <main
      class="mx-auto flex w-full max-w-[1100px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <div
        v-if="state.loading.value"
        class="h-80 animate-pulse rounded-lais bg-white"
      />
      <p
        v-else-if="state.error.value"
        class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ state.error.value }}
      </p>
      <RealEstateBrokerTaxDocumentForm
        v-else-if="state.request.value"
        v-model="state.documentKind.value"
        :file="state.file.value"
        :current-document="state.request.value.nota"
        :saving="state.saving.value"
        :progress="state.uploadProgress.value"
        :can-submit="state.canSubmitDocument.value"
        :can-download="Boolean(state.request.value.acoes?.pode_baixar_nota)"
        @update:file="state.file.value = $event"
        @submit="submit"
        @download="state.downloadDocument"
      />
      <div class="flex justify-end">
        <LaisPrimaryButton variant="ghost" @click="goBack">
          {{ t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.BACK') }}
        </LaisPrimaryButton>
      </div>
    </main>
    <RealEstateBrokerTaxDocumentSuccessDialog
      :show="successDialogVisible"
      @close="returnToDashboard"
    />
  </div>
</template>
